import {
  DataLayer,
  OfferType,
  Offers,
  ReplacementOffer,
} from '@dgx-pr-spa/shared/util';
import { offersType, planType } from '@shared/helpers/function.helpers';

export class GAOffersCommon {
  constructor(public readonly offers: Offers) {}

  get gaCommonData(): DataLayer {
    const offers = this.offers;
    const currentItem = offers?.current_item;
    const customerType = 'G';
    const currentPlan = offers?.current_plan;
    const contact = offers?.contact_details;
    const offer = offers?.offer;
    const PRUpgradeOffers = offer?.number_of_upgrades || 0;
    const PRFreeOffers = offer?.number_of_replacements || 0;

    const NumberOfItems = PRUpgradeOffers + PRFreeOffers;

    const PRGroup = 'aws';
    const productName =
      currentItem?.make && currentItem?.appliance_category
        ? `${currentItem.make} ${currentItem.appliance_category}`
        : undefined;

    const dataLayer: DataLayer = {
      pagename: 'product-replacement-offer',
      genericpagename: 'product-replacement-checkout',
      category: 'product-replacement',
      OfferType: offersType(offers)?.toString(),
      AccountBrand: currentItem?.make,
      Manufacturer: currentItem?.make,
      planNumber: currentPlan?.plan_number,
      productName,
      productModel: currentItem?.model,
      applianceName: currentItem?.appliance_category,
      customerType,
      PRFreeOffers,
      PRUpgradeOffers: offer?.number_of_upgrades,
      JobReferenceNumber: currentPlan?.reference_number,
      customerID: contact?.person_gid,
      PlanType: planType(currentPlan),
      NumberOfItems,
      PRGroup,
      upsell_flag: offer?.upsell_flag,
      upsell_reason: offer?.upsell_reason,
    };

    return dataLayer;
  }
}

export class GAOffersPageLoad extends GAOffersCommon {
  constructor(public readonly offers: Offers) {
    super(offers);
  }

  get gaData(): DataLayer {
    const dataLayer: DataLayer = {
      ...this.gaCommonData,
      event: 'PRAWS',
      eventPR: 'product-replacement-offer',
      PRStarted: 1,
    };

    return dataLayer;
  }
}

export class GAOffersCompareClick extends GAOffersCommon {
  constructor(
    private readonly comparison: ReplacementOffer,
    public readonly offers: Offers
  ) {
    super(offers);
  }

  get gaData() {
    const comparison = this.comparison;

    const compareProductName =
      comparison?.make && comparison?.appliance_category
        ? `${comparison.make} ${comparison.appliance_category}`
        : undefined;

    const eventPR =
      comparison?.offer_type === OfferType.REPLACEMENT
        ? 'compareProductAdd'
        : comparison?.offer_type === OfferType.UPGRADE
        ? 'compareEnhancedProductAdd'
        : undefined;

    const compareUpgrade =
      comparison?.offer_type === OfferType.UPGRADE
        ? comparison?.upgrade_fee
        : undefined;

    const dataLayer: DataLayer = {
      ...this.gaCommonData,
      event: 'PRGenericGAEvent',
      eventPR,
      compareProductName,
      compareUpgrade,
      AccountBrandOffer: comparison?.make,
      ManufacturerOffer: comparison?.make,
      rrp: comparison?.rrp,
    };

    return dataLayer;
  }
}

export class GAOffersShowMoreClick extends GAOffersCommon {
  constructor(
    public readonly offers: Offers,
    private readonly offerReplacementType: OfferType
  ) {
    super(offers);
  }

  get gaData() {
    const currentPlan = this.offers?.current_plan;
    const eventPR =
      this.offerReplacementType === OfferType.REPLACEMENT
        ? 'SeeMoreFreeOptions'
        : this.offerReplacementType === OfferType.UPGRADE
        ? 'SeeMoreUpgradeOptions'
        : undefined;

    const dataLayer: DataLayer = {
      ...this.gaCommonData,
      event: 'PRGenericGAEvent',
      eventPR,
      Action: eventPR,
      Label: currentPlan?.plan_number,
    };

    return dataLayer;
  }
}

export class GAOffersCompareModelsClick extends GAOffersCommon {
  constructor(public readonly offers: Offers) {
    super(offers);
  }
  get gaData() {
    const dataLayer: DataLayer = {
      ...this.gaCommonData,
      event: 'PRGenericGAEvent',
      eventPR: 'comparePreview',
    };

    return dataLayer;
  }
}

export class GAOffersCompareNowClick extends GAOffersCommon {
  constructor(
    public readonly offers: Offers,
    private readonly comparisons: ReplacementOffer[]
  ) {
    super(offers);
  }
  get gaData() {
    const offers = this.offers;
    const offer = offers?.offer;
    const comparisons = this.comparisons;

    let compareProductNameOffer = '';
    let compareUpgrade = '';
    let compareRrp = '';

    if (comparisons?.length) {
      comparisons.map((data, index) => {
        const pipe = index === comparisons.length - 1 ? '' : '|';
        compareProductNameOffer += `${data?.make} ${data?.appliance_category}${pipe}`;
        compareUpgrade += `${data?.upgrade_fee}${pipe}`;
        compareRrp += `${data?.rrp}${pipe}`;
      });
    }

    const dataLayer: DataLayer = {
      ...this.gaCommonData,
      event: 'PRAWS',
      eventPR: 'product-replacement-compare',
      pagename: 'product-replacement-compare',
      outstandingMonthlyPayments: offer?.outstanding_dd_count,
      remainingPayment: offer?.outstanding_dd_amount,
      compareProductNameOffer,
      compareUpgrade,
      rrp: compareRrp,
    };

    return dataLayer;
  }
}

export class GAOffersSpecDifferencesClick extends GAOffersCommon {
  constructor(
    public readonly offers: Offers,
    private readonly comparisons: ReplacementOffer[]
  ) {
    super(offers);
  }
  get gaData() {
    const offers = this.offers;
    const offer = offers?.offer;
    const comparisons = this.comparisons;
    const currentPlan = offers?.current_plan;

    let compareProductNameOffer = '';
    let compareUpgrade = '';
    let compareRrp = '';

    if (comparisons?.length) {
      comparisons.map((data, index) => {
        const pipe = index === comparisons.length - 1 ? '' : '|';
        compareProductNameOffer += `${data?.make} ${data?.appliance_category}${pipe}`;
        compareUpgrade += `${data?.upgrade_fee}${pipe}`;
        compareRrp += `${data?.rrp}${pipe}`;
      });
    }

    const dataLayer: DataLayer = {
      ...this.gaCommonData,
      event: 'PRAWS',
      eventPR: 'product-replacement-compare',
      pagename: 'product-replacement-compare',
      eventCategory: 'product-replacement',
      eventAction: 'show differences compare product',
      eventLabel: currentPlan?.plan_number,
      outstandingMonthlyPayments: offer?.outstanding_dd_count,
      remainingPayment: offer?.outstanding_dd_amount,
      compareProductNameOffer,
      compareUpgrade,
      rrp: compareRrp,
    };

    return dataLayer;
  }
}

export class GACompleteOrderNoDimensionError extends GAOffersCommon {
  constructor(public readonly offers: Offers) {
    super(offers);
  }

  get gaData(): DataLayer {
    const dataLayer: DataLayer = {
      ...this.gaCommonData,
      eventPR: 'pr-delivery-info',
      event: 'PRGenericGAEvent',
      eventCategory: 'Product Replacement',
      eventAction: 'product size - not available',
      pagename: 'product-replacement-checkout',
      genericpagename: 'product-replacement-checkout',
    };

    return dataLayer;
  }
}
